import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import cn from 'classnames';
// Types
import { TicketCheckoutEventFields } from 'api/ticketEvents/types/TicketCheckoutEventFields';
// Hooks
import { useAppContext } from 'hooks';
// Ui
import Button, { ButtonSize } from 'ui3/Button/Button';
// Styles
import styles from './GetTicketsButton.module.scss';

const GetTicketsModal = dynamic(
  () => import('../GetTicketsModal/GetTicketsModal'),
  { ssr: false }
);

type GetTicketsButtonProps = {
  event: TicketCheckoutEventFields;
  bordered?: boolean;
  size?: ButtonSize;
  className?: string;
  /** Shows a disabled Sold Out button instead of opening checkout. */
  soldOut?: boolean;
  /** Told when checkout (or the sign-in that leads to it) opens and closes. */
  onOpenChange?: (open: boolean) => void;
};

/** Opens checkout for the event's tickets; buying needs an account, so a visitor signs in first and checkout opens right after. */
const GetTicketsButton = ({
  event,
  bordered = false,
  size = 'medium',
  className,
  soldOut = false,
  onOpenChange,
}: GetTicketsButtonProps): JSX.Element => {
  const {
    token,
    showLoginModal,
    showSignUpModal,
    setLoginModalVisibility,
  } = useAppContext();
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [opensAfterSignIn, setOpensAfterSignIn] = useState(false);

  const openCheckout = () => {
    setHasOpened(true);
    setIsOpen(true);
  };

  useEffect(() => {
    if (!opensAfterSignIn) {
      return;
    }

    if (token?.accessToken) {
      setOpensAfterSignIn(false);
      openCheckout();
    } else if (!showLoginModal && !showSignUpModal) {
      setOpensAfterSignIn(false);
    }
  }, [opensAfterSignIn, token?.accessToken, showLoginModal, showSignUpModal]);

  const isActive = isOpen || opensAfterSignIn;

  useEffect(() => {
    if (!isActive || !onOpenChange) {
      return undefined;
    }

    onOpenChange(true);

    return () => onOpenChange(false);
  }, [isActive, onOpenChange]);

  const handleClick = () => {
    if (!token?.accessToken) {
      setOpensAfterSignIn(true);
      setLoginModalVisibility(true);
      return;
    }

    openCheckout();
  };

  return (
    <>
      <Button
        type="button"
        variant="secondary"
        size={size}
        icon="ticket"
        iconClassName={styles.icon}
        className={cn(styles.root, { [styles.bordered]: bordered }, className)}
        disabled={soldOut}
        onClick={handleClick}
      >
        {soldOut ? 'Sold Out' : 'Get Tickets'}
      </Button>
      {hasOpened && (
        <GetTicketsModal
          event={event}
          open={isOpen}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

export default GetTicketsButton;
